import React, { Component, ErrorInfo, ReactNode } from "react";
import styles from "../metamask-error-recovery/MetaMaskErrorRecovery.module.scss";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class WalletConnectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error(
      "Wallet connection error caught by error boundary:",
      error,
      errorInfo
    );

    this.setState({
      error,
      errorInfo,
    });

    // Check if this is a wallet-related error
    const isWalletError =
      error.message?.includes("wallet") ||
      error.message?.includes("MetaMask") ||
      error.message?.includes("OKX") ||
      error.message?.includes("ethereum") ||
      error.message?.includes("Connection") ||
      error.stack?.includes("useAppKit") ||
      error.stack?.includes("wagmi");

    if (isWalletError) {
      // Dispatch a custom event for wallet connection errors
      window.dispatchEvent(
        new CustomEvent("wallet-connection-error", {
          detail: { error, errorInfo },
        })
      );
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const isWalletError =
        this.state.error?.message?.includes("wallet") ||
        this.state.error?.message?.includes("MetaMask") ||
        this.state.error?.message?.includes("OKX") ||
        this.state.error?.message?.includes("ethereum") ||
        this.state.error?.message?.includes("Connection");

      if (isWalletError) {
        return (
          <div className={styles.overlay}>
            <div className={styles.modal}>
              <div className={styles.header}>
                <h3>Wallet Connection Error</h3>
              </div>

              <div className={styles.content}>
                <div className={styles.errorIcon}>⚠️</div>

                <div className={styles.message}>
                  <p>
                    A wallet connection error occurred that prevented the app
                    from loading properly.
                  </p>

                  <div className={styles.explanation}>
                    <p>
                      <strong>Error details:</strong>
                    </p>
                    <pre
                      style={{
                        fontSize: "12px",
                        background: "#f5f5f5",
                        padding: "10px",
                        borderRadius: "4px",
                        overflow: "auto",
                        maxHeight: "100px",
                      }}
                    >
                      {this.state.error?.message || "Unknown error"}
                    </pre>
                  </div>
                </div>

                <div className={styles.actions}>
                  <button
                    className={`${styles.button} ${styles.primary}`}
                    onClick={this.handleReload}
                  >
                    Reload Page
                  </button>

                  <button
                    className={`${styles.button} ${styles.secondary}`}
                    onClick={this.handleReset}
                  >
                    Try Again
                  </button>
                </div>

                <div className={styles.tips}>
                  <h4>Troubleshooting Tips:</h4>
                  <ul>
                    <li>Try refreshing the page</li>
                    <li>Clear your browser cache and cookies</li>
                    <li>Disable browser extensions temporarily</li>
                    <li>Try using a different browser</li>
                    <li>Check if your wallet extension is up to date</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      }
    }

    return this.props.children;
  }
}

export default WalletConnectionErrorBoundary;
